import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';

/** Public bucket holding profile photos, cover photos and club logos. */
export const PROFILE_IMAGES_BUCKET = 'profile-images';

/** Photos larger than this are refused before resizing: they are not camera pictures. */
const MAX_SOURCE_BYTES = 20 * 1024 * 1024;

type HumanImageKind = 'avatar' | 'cover';

/** Longest side, in pixels, of each stored image. */
const MAX_DIMENSIONS: Record<HumanImageKind | 'logo', number> = {
  avatar: 512,
  cover: 1600,
  logo: 512,
};

export class ImageUploadError extends Error {
  constructor(readonly reason: 'type' | 'size' | 'decode') {
    super(`Image rejected: ${reason}`);
  }
}

/**
 * Path of a file of the profile-images bucket from its public URL, or null for any other URL
 * (external links, legacy data URLs).
 */
export function storedImagePath(url: string | null | undefined): string | null {
  const marker = `/storage/v1/object/public/${PROFILE_IMAGES_BUCKET}/`;
  const index = url?.indexOf(marker) ?? -1;
  if (!url || index < 0) return null;
  return decodeURIComponent(url.slice(index + marker.length).split('?')[0]);
}

/**
 * Stores profile and club images in Supabase Storage after resizing them in the browser, and
 * returns their public URL (the value saved in image_url / cover_image_url).
 */
@Injectable({ providedIn: 'root' })
export class ImageUploadService {
  private readonly supabase = inject(SupabaseService);

  uploadHumanImage(humanId: string, kind: HumanImageKind, file: File): Promise<string> {
    return this.upload(`humans/${humanId}/${kind}`, file, MAX_DIMENSIONS[kind], false);
  }

  uploadClubLogo(clubId: string, file: File): Promise<string> {
    // Logos keep their transparency.
    return this.upload(`clubs/${clubId}/logo`, file, MAX_DIMENSIONS.logo, true);
  }

  /** Deletes a replaced image when it is one of ours; other URLs are left alone. */
  async removeStoredImage(url: string | null | undefined): Promise<void> {
    const path = storedImagePath(url);
    if (!path) return;
    const { error } = await this.supabase.client.storage.from(PROFILE_IMAGES_BUCKET).remove([path]);
    if (error) console.error('Could not delete the replaced image:', error);
  }

  private async upload(prefix: string, file: File, maxDimension: number, keepTransparency: boolean): Promise<string> {
    if (!file.type.startsWith('image/')) throw new ImageUploadError('type');
    if (file.size > MAX_SOURCE_BYTES) throw new ImageUploadError('size');
    const image = await resizeImage(file, maxDimension, keepTransparency);
    const extension = image.type === 'image/webp' ? 'webp' : image.type === 'image/png' ? 'png' : 'jpg';
    // A new name for each upload: browsers and the CDN can cache every file forever.
    const path = `${prefix}-${Date.now()}.${extension}`;
    const bucket = this.supabase.client.storage.from(PROFILE_IMAGES_BUCKET);
    const { error } = await bucket.upload(path, image, {
      contentType: image.type,
      cacheControl: '31536000',
      upsert: false,
    });
    if (error) throw error;
    return bucket.getPublicUrl(path).data.publicUrl;
  }
}

/**
 * Scales an image down so that its longest side is at most maxDimension, and encodes it as WebP
 * (or PNG/JPEG where the browser cannot encode WebP).
 */
async function resizeImage(file: File, maxDimension: number, keepTransparency: boolean): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new ImageUploadError('decode');
  }
  const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new ImageUploadError('decode');
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const encode = (type: string) => new Promise<Blob | null>(resolve => canvas.toBlob(resolve, type, 0.85));
  const webp = await encode('image/webp');
  // Browsers that cannot encode WebP return a PNG instead.
  if (webp?.type === 'image/webp') return webp;
  const fallback = await encode(keepTransparency ? 'image/png' : 'image/jpeg');
  if (!fallback) throw new ImageUploadError('decode');
  return fallback;
}
